'use client';

import { useDispatch, useSelector } from 'react-redux';
import { useCallback } from 'react';
import {
  updateLabel as updateLabelAction,
  updateDescription as updateDescriptionAction,
  updateTeams as updateTeamsAction,
  updateRefreshEvent as updateRefreshEventAction,
  updateMaxOpportunities as updateMaxOpportunitiesAction,
  setStrategyType as setStrategyTypeAction,
  setSelectedPlay as setSelectedPlayAction,
  updateScoutListData as updateScoutListDataAction,
  updateCodeSetData as updateCodeSetDataAction,
  updateFreshFacesData as updateFreshFacesDataAction,
  updateReferralsData as updateReferralsDataAction,
  updateNotReferralsData as updateNotReferralsDataAction,
  updatePatientJourneyData as updatePatientJourneyDataAction,
  updateTreatingVolumeData as updateTreatingVolumeDataAction,
  setFormMode as setFormModeAction,
  loadStrategy as loadStrategyAction,
  copyStrategy as copyStrategyAction,
  markAsSaved as markAsSavedAction,
  resetForm as resetFormAction,
  selectStrategyForm,
  selectFormData,
  selectSelectedType,
  selectSelectedPlay,
  selectScoutListData,
  selectCodeSetData,
  selectFreshFacesData,
  selectReferralsData,
  selectNotReferralsData,
  selectPatientJourneyData,
  selectTreatingVolumeData,
  selectHasUnsavedChanges,
  selectIsEditMode,
  selectIsCopyMode,
  selectStrategyId,
  StrategyType,
  SelectedPlay,
} from 'src/redux/slices/strategyFormSlice';
import {
  setDynamicCriteria,
  clearDynamicCriteria,
  selectDynamicCriteria,
} from 'src/redux/slices/aiListGeneratorSlice';
import { LOCATION } from 'src/hooks/filters/constants';
import {
  useGetTargetStrategyDetailsQuery,
  useGetEngageStrategyDetailsQuery,
} from 'src/api/redux';

type StrategyMode = 'target' | 'engage';

export const useStrategyForm = (strategyMode?: StrategyMode) => {
  const dispatch = useDispatch();

  // Selectors
  void useSelector(selectStrategyForm);
  const formData = useSelector(selectFormData);
  const selectedType = useSelector(selectSelectedType);
  const selectedPlay = useSelector(selectSelectedPlay);
  const scoutListData = useSelector(selectScoutListData);
  const codeSetData = useSelector(selectCodeSetData);
  const freshFacesData = useSelector(selectFreshFacesData);
  const referralsData = useSelector(selectReferralsData);
  const notReferralsData = useSelector(selectNotReferralsData);
  const patientJourneyData = useSelector(selectPatientJourneyData);
  const treatingVolumeData = useSelector(selectTreatingVolumeData);
  const hasUnsavedChanges = useSelector(selectHasUnsavedChanges);
  const isEditMode = useSelector(selectIsEditMode);
  const isCopyMode = useSelector(selectIsCopyMode);
  const strategyId = useSelector(selectStrategyId);

  // Get dynamic criteria from aiListGenerator slice for STRATEGY location
  const dynamicCriteria = useSelector(selectDynamicCriteria(LOCATION.STRATEGY));

  // Get strategy details if we have an ID - use appropriate endpoint based on strategy mode
  // If strategyMode is not provided, try target endpoint first, then engage if that fails
  const {
    currentData: targetStrategyDetails,
    isLoading: isLoadingTargetStrategy,
    error: targetError,
  } = useGetTargetStrategyDetailsQuery(strategyId, {
    skip: !strategyId || strategyMode === 'engage',
  });

  const {
    currentData: engageStrategyDetails,
    isLoading: isLoadingEngageStrategy,
  } = useGetEngageStrategyDetailsQuery(strategyId, {
    skip:
      !strategyId ||
      (strategyMode !== 'engage' && !targetError) ||
      strategyMode === 'target',
  });

  // Use the appropriate strategy details based on mode or what data is available
  const strategyDetails =
    strategyMode === 'engage'
      ? engageStrategyDetails
      : targetStrategyDetails || engageStrategyDetails;
  const isLoadingStrategyDetails =
    strategyMode === 'engage'
      ? isLoadingEngageStrategy
      : isLoadingTargetStrategy || isLoadingEngageStrategy;

  // Determine if this is an engage strategy (for edit mode when strategyMode is not provided)
  const isEngageStrategy =
    strategyMode === 'engage' ||
    (!targetStrategyDetails && !!engageStrategyDetails);

  // Actions
  const updateLabel = useCallback(
    (value: string) => {
      dispatch(updateLabelAction(value));
    },
    [dispatch]
  );

  const updateDescription = useCallback(
    (value: string) => {
      dispatch(updateDescriptionAction(value));
    },
    [dispatch]
  );

  const updateTeams = useCallback(
    (value: number[]) => {
      dispatch(updateTeamsAction(value));
    },
    [dispatch]
  );

  const updateRefreshEvent = useCallback(
    (value: 'NEVER' | 'MONTHLY') => {
      dispatch(updateRefreshEventAction(value));
    },
    [dispatch]
  );

  const updateMaxOpportunities = useCallback(
    (value: number) => {
      dispatch(updateMaxOpportunitiesAction(value));
    },
    [dispatch]
  );

  const setStrategyType = useCallback(
    (type: StrategyType | null) => {
      dispatch(setStrategyTypeAction(type));
    },
    [dispatch]
  );

  const setSelectedPlay = useCallback(
    (play: SelectedPlay | null) => {
      dispatch(setSelectedPlayAction(play));
    },
    [dispatch]
  );

  const updateScoutListData = useCallback(
    (data: any) => {
      dispatch(updateScoutListDataAction(data));
    },
    [dispatch]
  );

  const updateCodeSetData = useCallback(
    (data: any) => {
      dispatch(updateCodeSetDataAction(data));
    },
    [dispatch]
  );

  const updateFreshFacesData = useCallback(
    (data: any) => {
      dispatch(updateFreshFacesDataAction(data));
    },
    [dispatch]
  );

  const updateReferralsData = useCallback(
    (data: any) => {
      dispatch(updateReferralsDataAction(data));
    },
    [dispatch]
  );

  const updateNotReferralsData = useCallback(
    (data: any) => {
      dispatch(updateNotReferralsDataAction(data));
    },
    [dispatch]
  );

  const updatePatientJourneyData = useCallback(
    (data: any) => {
      dispatch(updatePatientJourneyDataAction(data));
    },
    [dispatch]
  );

  const updateTreatingVolumeData = useCallback(
    (data: any) => {
      dispatch(updateTreatingVolumeDataAction(data));
    },
    [dispatch]
  );

  const setFormMode = useCallback(
    (mode: {
      isEditMode: boolean;
      isCopyMode: boolean;
      strategyId?: string;
    }) => {
      dispatch(setFormModeAction(mode));
    },
    [dispatch]
  );

  const loadStrategy = useCallback(
    (strategy: any) => {
      dispatch(loadStrategyAction(strategy));

      // Hydrate type-specific data for edit mode
      if (strategy.type === 'SCOUT_LIST') {
        let criteriaData: any;

        if (strategy.criteria_template) {
          criteriaData = {
            criteria_template: strategy.criteria_template,
            criteria_inputs: strategy.criteria_inputs || {},
            listId: strategy.id,
          };
        } else if (strategy.template) {
          criteriaData = {
            criteria_template: strategy.template.criteria_template || '',
            criteria_inputs: strategy.template.criteria_inputs || {},
            listId: strategy.id,
          };
        } else if (strategy.criteria) {
          criteriaData = {
            criteria_template: strategy.criteria,
            criteria_inputs: {},
            listId: strategy.id,
          };
        }

        if (criteriaData) {
          dispatch(
            setDynamicCriteria({
              criteria: criteriaData,
              location: LOCATION.STRATEGY,
            })
          );
        }
      } else if (strategy.type === 'FRESH_FACES') {
        // Hydrate FRESH_FACES specific data - preserve full objects for display
        dispatch(
          updateFreshFacesDataAction({
            code_set: strategy.code_set, // Keep full object or ID as-is
            filter_set: strategy.filter_set, // Keep full object or ID as-is
            volume_type: strategy.volume_type || 'treating',
            provider_type: strategy.provider_type || 'hcp',
          })
        );
        // Clear dynamic criteria since FRESH_FACES doesn't use them
        dispatch(clearDynamicCriteria({ location: LOCATION.STRATEGY }));
      } else if (strategy.type === 'REFERRALS') {
        let criteriaData: any;

        if (strategy.criteria_template) {
          criteriaData = {
            criteria_template: strategy.criteria_template,
            criteria_inputs: strategy.criteria_inputs || {},
            listId: strategy.id,
          };
        } else if (strategy.template) {
          criteriaData = {
            criteria_template: strategy.template.criteria_template || '',
            criteria_inputs: strategy.template.criteria_inputs || {},
            listId: strategy.id,
          };
        } else if (strategy.criteria) {
          criteriaData = {
            criteria_template: strategy.criteria,
            criteria_inputs: {},
            listId: strategy.id,
          };
        }

        if (criteriaData) {
          dispatch(
            setDynamicCriteria({
              criteria: criteriaData,
              location: LOCATION.STRATEGY,
            })
          );
        }
      } else if (strategy.type === 'NOT_REFERRING') {
        let criteriaData: any;

        if (strategy.criteria_template) {
          criteriaData = {
            criteria_template: strategy.criteria_template,
            criteria_inputs: strategy.criteria_inputs || {},
            listId: strategy.id,
          };
        } else if (strategy.template) {
          criteriaData = {
            criteria_template: strategy.template.criteria_template || '',
            criteria_inputs: strategy.template.criteria_inputs || {},
            listId: strategy.id,
          };
        } else if (strategy.criteria) {
          criteriaData = {
            criteria_template: strategy.criteria,
            criteria_inputs: {},
            listId: strategy.id,
          };
        }

        if (criteriaData) {
          dispatch(
            setDynamicCriteria({
              criteria: criteriaData,
              location: LOCATION.STRATEGY,
            })
          );
        }

        // Update NOT_REFERRING specific data including radius
        dispatch(
          updateNotReferralsDataAction({
            radius: strategy.radius ?? 0,
          })
        );
      } else if (strategy.type === 'PATIENT_JOURNEY') {
        // Clear main strategy location
        dispatch(clearDynamicCriteria({ location: LOCATION.STRATEGY }));

        // Populate index event criteria
        if (strategy.index_event_template) {
          dispatch(
            setDynamicCriteria({
              criteria: {
                criteria_template:
                  strategy.index_event_template.criteria_template || '',
                criteria_inputs:
                  strategy.index_event_template.criteria_inputs || {},
              },
              location: LOCATION.PATIENT_JOURNEY_INDEX_EVENT,
            })
          );
        }

        // Populate target event criteria
        if (strategy.target_event_template) {
          dispatch(
            setDynamicCriteria({
              criteria: {
                criteria_template:
                  strategy.target_event_template.criteria_template || '',
                criteria_inputs:
                  strategy.target_event_template.criteria_inputs || {},
              },
              location: LOCATION.PATIENT_JOURNEY_TARGET_EVENT,
            })
          );
        }

        // Update PATIENT_JOURNEY specific data including radius from index_event_radius
        dispatch(
          updatePatientJourneyDataAction({
            radius: strategy.index_event_radius ?? 0,
            target_event_interval: strategy.target_event_interval,
          })
        );
      } else if (strategy.type === 'TREATING_VOLUME') {
        let criteriaData: any;

        if (strategy.criteria_template) {
          criteriaData = {
            criteria_template: strategy.criteria_template,
            criteria_inputs: strategy.criteria_inputs || {},
            listId: strategy.id,
          };
        } else if (strategy.template) {
          criteriaData = {
            criteria_template: strategy.template.criteria_template || '',
            criteria_inputs: strategy.template.criteria_inputs || {},
            listId: strategy.id,
          };
        } else if (strategy.criteria) {
          criteriaData = {
            criteria_template: strategy.criteria,
            criteria_inputs: {},
            listId: strategy.id,
          };
        }

        if (criteriaData) {
          dispatch(
            setDynamicCriteria({
              criteria: criteriaData,
              location: LOCATION.STRATEGY,
            })
          );
        }
      } else {
        // Clear dynamic criteria for other types
        dispatch(clearDynamicCriteria({ location: LOCATION.STRATEGY }));
      }
    },
    [dispatch]
  );

  const copyStrategyToForm = useCallback(
    (strategyDetails: any) => {
      dispatch(copyStrategyAction(strategyDetails));

      // Update the AI generator slice for scout list
      if (strategyDetails.type === 'SCOUT_LIST') {
        let criteriaData: any;

        if (strategyDetails.criteria_template) {
          criteriaData = {
            criteria_template: strategyDetails.criteria_template,
            criteria_inputs: strategyDetails.criteria_inputs || {},
            listId: strategyDetails.id,
          };
        } else if (strategyDetails.template) {
          criteriaData = {
            criteria_template: strategyDetails.template.criteria_template || '',
            criteria_inputs: strategyDetails.template.criteria_inputs || {},
            listId: strategyDetails.id,
          };
        } else if (strategyDetails.criteria) {
          criteriaData = {
            criteria_template: strategyDetails.criteria,
            criteria_inputs: {},
            listId: strategyDetails.id,
          };
        }

        if (criteriaData) {
          dispatch(
            setDynamicCriteria({
              criteria: criteriaData,
              location: LOCATION.STRATEGY,
            })
          );
        }
      } else if (strategyDetails.type === 'FRESH_FACES') {
        // Hydrate FRESH_FACES specific data - preserve full objects for display
        dispatch(
          updateFreshFacesDataAction({
            code_set: strategyDetails.code_set, // Keep full object or ID as-is
            filter_set: strategyDetails.filter_set, // Keep full object or ID as-is
            volume_type: strategyDetails.volume_type || 'treating',
            provider_type: strategyDetails.provider_type || 'hcp',
          })
        );
        // Clear dynamic criteria since FRESH_FACES doesn't use them
        dispatch(clearDynamicCriteria({ location: LOCATION.STRATEGY }));
      } else if (strategyDetails.type === 'REFERRALS') {
        let criteriaData: any;

        if (strategyDetails.criteria_template) {
          criteriaData = {
            criteria_template: strategyDetails.criteria_template,
            criteria_inputs: strategyDetails.criteria_inputs || {},
            listId: strategyDetails.id,
          };
        } else if (strategyDetails.template) {
          criteriaData = {
            criteria_template: strategyDetails.template.criteria_template || '',
            criteria_inputs: strategyDetails.template.criteria_inputs || {},
            listId: strategyDetails.id,
          };
        } else if (strategyDetails.criteria) {
          criteriaData = {
            criteria_template: strategyDetails.criteria,
            criteria_inputs: {},
            listId: strategyDetails.id,
          };
        }

        if (criteriaData) {
          dispatch(
            setDynamicCriteria({
              criteria: criteriaData,
              location: LOCATION.STRATEGY,
            })
          );
        }
      } else if (strategyDetails.type === 'NOT_REFERRING') {
        let criteriaData: any;

        if (strategyDetails.criteria_template) {
          criteriaData = {
            criteria_template: strategyDetails.criteria_template,
            criteria_inputs: strategyDetails.criteria_inputs || {},
            listId: strategyDetails.id,
          };
        } else if (strategyDetails.template) {
          criteriaData = {
            criteria_template: strategyDetails.template.criteria_template || '',
            criteria_inputs: strategyDetails.template.criteria_inputs || {},
            listId: strategyDetails.id,
          };
        } else if (strategyDetails.criteria) {
          criteriaData = {
            criteria_template: strategyDetails.criteria,
            criteria_inputs: {},
            listId: strategyDetails.id,
          };
        }

        if (criteriaData) {
          dispatch(
            setDynamicCriteria({
              criteria: criteriaData,
              location: LOCATION.STRATEGY,
            })
          );
        }

        // Copy NOT_REFERRING specific data including radius
        dispatch(
          updateNotReferralsDataAction({
            radius: strategyDetails.radius ?? 0,
          })
        );
      } else if (strategyDetails.type === 'PATIENT_JOURNEY') {
        // Clear main strategy location
        dispatch(clearDynamicCriteria({ location: LOCATION.STRATEGY }));

        // Populate index event criteria
        if (strategyDetails.index_event_template) {
          dispatch(
            setDynamicCriteria({
              criteria: {
                criteria_template:
                  strategyDetails.index_event_template.criteria_template || '',
                criteria_inputs:
                  strategyDetails.index_event_template.criteria_inputs || {},
              },
              location: LOCATION.PATIENT_JOURNEY_INDEX_EVENT,
            })
          );
        }

        // Populate target event criteria
        if (strategyDetails.target_event_template) {
          dispatch(
            setDynamicCriteria({
              criteria: {
                criteria_template:
                  strategyDetails.target_event_template.criteria_template || '',
                criteria_inputs:
                  strategyDetails.target_event_template.criteria_inputs || {},
              },
              location: LOCATION.PATIENT_JOURNEY_TARGET_EVENT,
            })
          );
        }

        // Copy PATIENT_JOURNEY specific data including radius from index_event_radius
        dispatch(
          updatePatientJourneyDataAction({
            radius: strategyDetails.index_event_radius ?? 0,
            target_event_interval: strategyDetails.target_event_interval,
          })
        );
      } else if (strategyDetails.type === 'TREATING_VOLUME') {
        let criteriaData: any;

        if (strategyDetails.criteria_template) {
          criteriaData = {
            criteria_template: strategyDetails.criteria_template,
            criteria_inputs: strategyDetails.criteria_inputs || {},
            listId: strategyDetails.id,
          };
        } else if (strategyDetails.template) {
          criteriaData = {
            criteria_template: strategyDetails.template.criteria_template || '',
            criteria_inputs: strategyDetails.template.criteria_inputs || {},
            listId: strategyDetails.id,
          };
        } else if (strategyDetails.criteria) {
          criteriaData = {
            criteria_template: strategyDetails.criteria,
            criteria_inputs: {},
            listId: strategyDetails.id,
          };
        }

        if (criteriaData) {
          dispatch(
            setDynamicCriteria({
              criteria: criteriaData,
              location: LOCATION.STRATEGY,
            })
          );
        }
      } else {
        // Clear dynamic criteria for other types
        dispatch(clearDynamicCriteria({ location: LOCATION.STRATEGY }));
      }
    },
    [dispatch]
  );

  const markAsSaved = useCallback(() => {
    dispatch(markAsSavedAction());
  }, [dispatch]);

  const resetForm = useCallback(() => {
    dispatch(resetFormAction());
    dispatch(clearDynamicCriteria({ location: LOCATION.STRATEGY }));
    // Clear Patient Journey specific locations
    dispatch(
      clearDynamicCriteria({ location: LOCATION.PATIENT_JOURNEY_INDEX_EVENT })
    );
    dispatch(
      clearDynamicCriteria({ location: LOCATION.PATIENT_JOURNEY_TARGET_EVENT })
    );
  }, [dispatch]);

  // Helper function to transform criteria_inputs: this_site -> this_provider
  const transformCriteriaInputs = useCallback((inputs: any) => {
    if (!inputs) return {};

    const transformed: any = {};
    Object.entries(inputs).forEach(([key, value]: [string, any]) => {
      if (value?.type === 'this_site') {
        // Transform this_site to this_provider for the API
        transformed[key] = {
          ...value,
          type: 'this_provider',
          ui_key: 'this_site',
        };
      } else {
        transformed[key] = value;
      }
    });
    return transformed;
  }, []);

  // Combined data for form submission
  const getFormDataForSubmission = useCallback(() => {
    // For engage strategies, exclude target-specific fields (teams, refresh_event)
    const baseData = isEngageStrategy
      ? {
          label: formData.label,
          description: formData.description,
          type: selectedType,
          max_opportunities: formData.max_opportunities,
        }
      : {
          label: formData.label,
          description: formData.description,
          type: selectedType,
          teams: formData.teams,
          refresh_event: formData.refresh_event,
          max_opportunities: formData.max_opportunities,
        };

    // Add type-specific data
    if (selectedType === 'SCOUT_LIST') {
      return {
        ...baseData,
        criteria_template: dynamicCriteria?.criteria_template || '',
        criteria_inputs: dynamicCriteria?.criteria_inputs || {},
      };
    } else if (selectedType === 'FRESH_FACES') {
      return {
        ...baseData,
        code_set:
          typeof freshFacesData?.code_set === 'object'
            ? freshFacesData.code_set.id
            : freshFacesData?.code_set,
        filter_set:
          typeof freshFacesData?.filter_set === 'object'
            ? freshFacesData.filter_set.id
            : freshFacesData?.filter_set,
        volume_type: freshFacesData?.volume_type,
        provider_type: freshFacesData?.provider_type || 'hcp',
      };
    } else if (selectedType === 'REFERRALS') {
      return {
        ...baseData,
        criteria_template: dynamicCriteria?.criteria_template || '',
        criteria_inputs: transformCriteriaInputs(
          dynamicCriteria?.criteria_inputs
        ),
      };
    } else if (selectedType === 'NOT_REFERRING') {
      return {
        ...baseData,
        criteria_template: dynamicCriteria?.criteria_template || '',
        criteria_inputs: transformCriteriaInputs(
          dynamicCriteria?.criteria_inputs
        ),
        radius: notReferralsData?.radius ?? 0,
      };
    } else if (selectedType === 'PATIENT_JOURNEY') {
      // Return base data for PATIENT_JOURNEY when implemented
      return baseData;
    } else if (selectedType === 'TREATING_VOLUME') {
      // Return base data for TREATING_VOLUME when implemented
      return {
        ...baseData,
        criteria_template: dynamicCriteria?.criteria_template || '',
        criteria_inputs: transformCriteriaInputs(
          dynamicCriteria?.criteria_inputs
        ),
      };
    }

    return baseData;
  }, [
    formData,
    selectedType,
    dynamicCriteria,
    codeSetData,
    freshFacesData,
    notReferralsData,
    isEngageStrategy,
    transformCriteriaInputs,
  ]);

  return {
    // State
    formData,
    selectedType,
    selectedPlay,
    scoutListData,
    codeSetData,
    freshFacesData,
    referralsData,
    notReferralsData,
    patientJourneyData,
    treatingVolumeData,
    hasUnsavedChanges,
    isEditMode,
    isCopyMode,
    strategyId,
    dynamicCriteria,
    strategyDetails,
    isLoadingStrategyDetails,
    isEngageStrategy,

    // Actions
    updateLabel,
    updateDescription,
    updateTeams,
    updateRefreshEvent,
    updateMaxOpportunities,
    setStrategyType,
    setSelectedPlay,
    updateScoutListData,
    updateCodeSetData,
    updateFreshFacesData,
    updateReferralsData,
    updateNotReferralsData,
    updatePatientJourneyData,
    updateTreatingVolumeData,
    setFormMode,
    loadStrategy,
    copyStrategy: copyStrategyToForm,
    markAsSaved,
    resetForm,
    getFormDataForSubmission,
    transformCriteriaInputs,
  };
};

export default useStrategyForm;
